import type {
  ActividadGrupal,
  CampoDocumentoAsistenciaGrupal,
} from '../services/actividad-grupal.service';

/** Metadato normalizado del documento de asistencia/evidencia (listado / detalle). */
export interface ActividadGrupalAsistenciaAdjunto {
  ruta_almacenamiento: string;
  nombre_archivo: string;
  mime_type?: string | null;
  fecha_subida?: string | null;
  tamano_bytes?: number | null;
  url_descarga?: string | null;
}

export type { CampoDocumentoAsistenciaGrupal };

export const CAMPOS_DOCUMENTO_ASISTENCIA_GRUPAL: readonly {
  campo: CampoDocumentoAsistenciaGrupal;
  etiqueta: string;
  nombreCorto: string;
  icono: string;
  badgeDetalle: string;
}[] = [
  {
    campo: 'documento_asistencia',
    etiqueta: 'Subir Registro de Asistencia',
    nombreCorto: 'registro de asistencia',
    icono: 'clipboard-outline',
    badgeDetalle: 'REGISTRO DE ASISTENCIA',
  },
  {
    campo: 'documento_evidencia',
    etiqueta: 'Subir Evidencia Fotográfica',
    nombreCorto: 'evidencia fotográfica',
    icono: 'camera-outline',
    badgeDetalle: 'EVIDENCIA',
  },
  {
    campo: 'documento_otro',
    etiqueta: 'Subir Otro Archivo (Opcional)',
    nombreCorto: 'otro archivo',
    icono: 'attach-outline',
    badgeDetalle: 'OTRO DOCUMENTO',
  },
];

function adjuntoDesdeObjetoApi(o: Record<string, unknown>): ActividadGrupalAsistenciaAdjunto | null {
  const rutaRaw = o['ruta_almacenamiento'] ?? o['rutaAlmacenamiento'] ?? o['ruta'];
  const ruta = rutaRaw != null ? String(rutaRaw).trim() : '';
  if (!ruta) return null;

  const nombreRaw =
    o['nombre_archivo'] ?? o['nombreArchivo'] ?? o['nombre'] ?? o['filename'];
  const nombre = nombreRaw != null ? String(nombreRaw).trim() : '';

  const mimeRaw = o['mime_type'] ?? o['mimeType'];
  const urlRaw = o['url_descarga'] ?? o['urlDescarga'];
  const fechaRaw = o['fecha_subida'] ?? o['fechaSubida'];
  const tamanoRaw = o['tamano_bytes'] ?? o['tamanoBytes'];

  return {
    ruta_almacenamiento: ruta,
    nombre_archivo: nombre || ruta.split('/').pop() || 'documento',
    mime_type: mimeRaw != null ? String(mimeRaw) : null,
    fecha_subida: fechaRaw != null ? String(fechaRaw) : null,
    tamano_bytes: tamanoRaw != null ? Number(tamanoRaw) : null,
    url_descarga: urlRaw != null ? String(urlRaw) : null,
  };
}

/**
 * Extrae metadatos de un documento por campo desde un ítem de actividad grupal.
 * Fuente: `detalle.<campo>` del listado.
 */
export function extraerAdjuntoPorCampo(
  actividad: ActividadGrupal | null | undefined,
  campo: CampoDocumentoAsistenciaGrupal
): ActividadGrupalAsistenciaAdjunto | null {
  if (!actividad?.detalle) return null;

  const docDetalle = actividad.detalle[campo];
  if (docDetalle != null && typeof docDetalle === 'object') {
    return adjuntoDesdeObjetoApi(docDetalle as Record<string, unknown>);
  }

  return null;
}

/**
 * Extrae metadatos del documento de asistencia desde un ítem de actividad grupal.
 * Alias de `extraerAdjuntoPorCampo(..., 'documento_asistencia')`.
 */
export function extraerAdjuntoAsistenciaDesdeActividad(
  actividad: ActividadGrupal | null | undefined
): ActividadGrupalAsistenciaAdjunto | null {
  return extraerAdjuntoPorCampo(actividad, 'documento_asistencia');
}

/** Lista de adjuntos presentes en servidor (hasta 3). */
export function extraerAdjuntosDesdeActividad(
  actividad: ActividadGrupal | null | undefined
): { campo: CampoDocumentoAsistenciaGrupal; etiqueta: string; badgeDetalle: string; adjunto: ActividadGrupalAsistenciaAdjunto }[] {
  if (!actividad) return [];

  const resultado: {
    campo: CampoDocumentoAsistenciaGrupal;
    etiqueta: string;
    badgeDetalle: string;
    adjunto: ActividadGrupalAsistenciaAdjunto;
  }[] = [];

  for (const { campo, etiqueta, badgeDetalle } of CAMPOS_DOCUMENTO_ASISTENCIA_GRUPAL) {
    const adjunto = extraerAdjuntoPorCampo(actividad, campo);
    if (adjunto) {
      resultado.push({ campo, etiqueta, badgeDetalle, adjunto });
    }
  }

  return resultado;
}

/** Indica si la actividad grupal ya tiene al menos un documento en servidor. */
export function actividadGrupalTieneDocumentoAsistencia(
  actividad: ActividadGrupal | null | undefined
): boolean {
  return CAMPOS_DOCUMENTO_ASISTENCIA_GRUPAL.some(
    ({ campo }) => extraerAdjuntoPorCampo(actividad, campo) != null
  );
}

/** Estado «Realizada» (misma heurística que badges en listado/detalle). */
export function esEstadoActividadGrupalRealizada(estado: string | null | undefined): boolean {
  const e = (estado ?? '').toLowerCase();
  return e.includes('realiza') || e.includes('completad') || e.includes('finaliz');
}
